'use client'

import { Button } from '@/components/ui/button'
import { ImageHelper } from '@/lib/helpers/image.helper'
import { cn } from '@/lib/utils'
import useEmblaCarousel from 'embla-carousel-react'
import Image from 'next/image'
import 'photoswipe/dist/photoswipe.css'
import PhotoSwipeLightbox from 'photoswipe/lightbox'
import 'photoswipe/style.css'
import { useCallback, useEffect, useState } from 'react'

export type ImageType = {
  url: string
  alt: string
}

interface GalleryProps {
  images: ImageType[]
  className?: string
}

export const ProductInfoSuggestGallery: React.FC<GalleryProps> = ({
  images,
  className,
}) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: false })
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [canScrollPrev, setCanScrollPrev] = useState(false)
  const [canScrollNext, setCanScrollNext] = useState(false)

  const scrollTo = useCallback(
    (index: number) => {
      if (emblaApi) emblaApi.scrollTo(index)
    },
    [emblaApi]
  )

  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi])
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi])

  const onSelect = useCallback(() => {
    if (!emblaApi) return
    const newIndex = emblaApi.selectedScrollSnap()
    setSelectedIndex(newIndex)
    setCanScrollPrev(emblaApi.canScrollPrev())
    setCanScrollNext(emblaApi.canScrollNext())
  }, [emblaApi])

  useEffect(() => {
    if (!emblaApi) return
    emblaApi.on('select', onSelect)
    onSelect()

    return () => {
      emblaApi.off('select', onSelect)
    }
  }, [emblaApi, onSelect])

  useEffect(() => {
    let lightbox: PhotoSwipeLightbox | null = new PhotoSwipeLightbox({
      gallery: '#suggest-product-gallery',
      children: 'a',
      pswpModule: () => import('photoswipe'),
    })
    lightbox.init()

    return () => {
      if (lightbox) {
        lightbox.destroy()
        lightbox = null
      }
    }
  }, [])

  if (!images || images.length === 0) return null

  return (
    <section className={cn('relative', className)}>
      <div className='no-scrollbar absolute left-5 top-6 z-[1] flex max-h-[calc(100vh-400px)] flex-col gap-2 overflow-y-auto lg:static lg:gap-1.5'>
        {images?.map((item, index) => (
          <button
            key={index}
            className={cn(
              'h-10 min-h-10 w-10 min-w-10 overflow-hidden rounded border border-neutral-300 lg:h-14.5 lg:w-11 lg:border-0',
              selectedIndex !== index && 'opacity-50'
            )}
            onClick={() => scrollTo(index)}
          >
            <Image
              src={ImageHelper.getImageUrl(item.url, '270_306')}
              alt={item.alt || `Product side ${index + 1}`}
              width={30}
              height={40}
              className='h-auto w-full object-cover'
              unoptimized
            />
          </button>
        ))}
      </div>

      <div
        className='relative h-full w-full overflow-hidden md:rounded-lg'
        ref={emblaRef}
      >
        <div className='pswp-gallery flex gap-2' id='suggest-product-gallery'>
          {images?.map((item, index) => (
            <a
              href={ImageHelper.getImageUrl(item.url)}
              data-pswp-width={800}
              data-pswp-height={1200}
              key={`suggest-gallery-${index}`}
              target='_blank'
              rel='noreferrer'
              className='relative block flex-[0_0_100%] cursor-zoom-in'
            >
              <Image
                src={ImageHelper.getImageUrl(item.url, 'xl', 713, 1050, 95)}
                alt={item.alt || `Product Image ${index + 1}`}
                width={713}
                height={1050}
                className='h-full w-full object-cover'
                fetchPriority={index === 0 ? 'high' : 'low'}
                sizes='(max-width: 768px) 100vw, 375px'
                unoptimized={index === 0}
                priority={index === 0}
              />
            </a>
          ))}
        </div>
      </div>

      {(canScrollPrev || canScrollNext) && (
        <div className='absolute bottom-4 right-4'>
          <Button
            size='icon'
            onClick={scrollPrev}
            disabled={!canScrollPrev}
            variant='white'
            className='mr-2'
          >
            <Image
              alt='Icon prev'
              src='/icons/icon-arrow-line-right.svg'
              width={16}
              height={16}
              className='rotate-180 invert'
            />
          </Button>

          <Button
            size='icon'
            onClick={scrollNext}
            disabled={!canScrollNext}
            variant='white'
          >
            <Image
              alt='Icon prev'
              src='/icons/icon-arrow-line-right.svg'
              width={16}
              height={16}
              className='invert'
            />
          </Button>
        </div>
      )}
    </section>
  )
}
